import React, { useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, PackageSearch, Phone, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

// Shared emergency + lost/found actions for riders and drivers.
// props:
//   idPayload  – object identifying the actor, e.g. { role:"rider", session_id } | { role:"rider", booking_id, phone } | { role:"driver", phone }
//   reportKind – "lost" (rider) | "found" (driver)
//   testPrefix – unique data-testid prefix
export function SafetyActions({ idPayload, reportKind, testPrefix = "safety", showSOS = true }) {
  const [sosOpen, setSosOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [note, setNote] = useState("");
  const [description, setDescription] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [busy, setBusy] = useState(false);

  const sendSOS = async () => {
    setBusy(true);
    const post = (coords) => api.post("/sos", { ...idPayload, note: note.trim() || undefined, ...coords });
    const finish = (ok) => {
      setBusy(false);
      if (ok) { toast.success("Dispatch has been alerted. Stay safe — call 911 if this is life-threatening."); setSosOpen(false); setNote(""); }
    };
    try {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (p) => { try { await post({ lat: p.coords.latitude, lng: p.coords.longitude }); finish(true); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not alert dispatch"); setBusy(false); } },
          async () => { try { await post({}); finish(true); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not alert dispatch"); setBusy(false); } },
          { enableHighAccuracy: true, timeout: 8000 }
        );
      } else {
        await post({}); finish(true);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not alert dispatch");
      setBusy(false);
    }
  };

  const sendReport = async () => {
    if (!description.trim()) { toast.error("Please describe the item"); return; }
    setBusy(true);
    try {
      await api.post("/lost-found", { ...idPayload, kind: reportKind, description: description.trim(), contact_phone: contactPhone.trim() || undefined });
      toast.success(reportKind === "lost" ? "Reported — we'll check the vehicle and get back to you." : "Thanks — the item is logged for return.");
      setReportOpen(false); setDescription(""); setContactPhone("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not submit");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="grid grid-cols-2 gap-3 mt-3">
        {showSOS && (
          <button onClick={() => setSosOpen(true)} className="label text-xs border border-red-500/50 text-red-400 py-2.5 flex items-center justify-center gap-1.5 hover:bg-red-500/10 transition-colors duration-300" data-testid={`${testPrefix}-sos-btn`}>
            <ShieldAlert size={14} /> Emergency
          </button>
        )}
        <button onClick={() => setReportOpen(true)} className={`label text-xs border border-white/20 text-white/70 py-2.5 flex items-center justify-center gap-1.5 hover:border-gold hover:text-gold transition-colors duration-300 ${showSOS ? "" : "col-span-2"}`} data-testid={`${testPrefix}-report-btn`}>
          <PackageSearch size={14} /> {reportKind === "lost" ? "Lost item" : "Found item"}
        </button>
      </div>

      {sosOpen && (
        <div className="fixed inset-0 z-[110] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSosOpen(false)} data-testid={`${testPrefix}-sos-modal`}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md border border-red-500/40 bg-[var(--nl-bg-2)] p-8 space-y-5 relative">
            <button onClick={() => setSosOpen(false)} className="absolute top-4 right-4 text-white/40 hover:text-white" data-testid={`${testPrefix}-sos-close`}><X size={18} /></button>
            <div>
              <p className="label text-red-400 mb-1 flex items-center gap-2"><ShieldAlert size={16} /> Emergency</p>
              <h3 className="font-serif font-light text-2xl text-white">Need help right now?</h3>
              <p className="text-white/50 font-body text-sm mt-2">If this is life-threatening, call 911 immediately. You can also alert NamarckLimo dispatch — we'll get your ride details and location.</p>
            </div>
            <a href="tel:911" className="w-full label bg-red-500 text-white py-3.5 flex items-center justify-center gap-2 hover:bg-red-600 transition-colors duration-300" data-testid={`${testPrefix}-call-911`}>
              <Phone size={16} /> Call 911
            </a>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Optional: tell dispatch what's happening" className="w-full bg-[var(--nl-bg-3)] border border-white/10 px-4 py-2.5 text-white font-body text-sm focus:outline-none focus:border-gold resize-none" data-testid={`${testPrefix}-sos-note`} />
            <button onClick={sendSOS} disabled={busy} className="w-full label border border-red-500/50 text-red-400 py-3 hover:bg-red-500/10 transition-colors duration-300 disabled:opacity-50" data-testid={`${testPrefix}-sos-alert`}>
              {busy ? "Alerting…" : "Alert NamarckLimo dispatch"}
            </button>
          </div>
        </div>
      )}

      {reportOpen && (
        <div className="fixed inset-0 z-[110] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setReportOpen(false)} data-testid={`${testPrefix}-report-modal`}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md border border-white/10 bg-[var(--nl-bg-2)] p-8 space-y-5 relative">
            <button onClick={() => setReportOpen(false)} className="absolute top-4 right-4 text-white/40 hover:text-white" data-testid={`${testPrefix}-report-close`}><X size={18} /></button>
            <div>
              <p className="label text-gold mb-1 flex items-center gap-2"><PackageSearch size={16} /> {reportKind === "lost" ? "Report a lost item" : "Report a found item"}</p>
              <h3 className="font-serif font-light text-2xl text-white">{reportKind === "lost" ? "Left something behind?" : "Found in the vehicle?"}</h3>
            </div>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Describe the item — e.g. black leather wallet, iPhone 15, blue scarf…" className="w-full bg-[var(--nl-bg-3)] border border-white/10 px-4 py-2.5 text-white font-body text-sm focus:outline-none focus:border-gold resize-none" data-testid={`${testPrefix}-report-desc`} />
            <input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="Best contact number (optional)" className="w-full bg-[var(--nl-bg-3)] border border-white/10 px-4 py-2.5 text-white font-body text-sm focus:outline-none focus:border-gold" data-testid={`${testPrefix}-report-phone`} />
            <div className="flex gap-3">
              <button onClick={() => setReportOpen(false)} className="flex-1 label border border-white/15 text-white/60 py-3 hover:text-white transition-colors duration-300" data-testid={`${testPrefix}-report-dismiss`}>Cancel</button>
              <button onClick={sendReport} disabled={busy} className="flex-1 label bg-gold text-black py-3 hover:bg-[var(--nl-gold-hover)] transition-colors duration-300 disabled:opacity-50" data-testid={`${testPrefix}-report-submit`}>{busy ? "Submitting…" : "Submit report"}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
